import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { PiArrowCounterClockwise } from 'react-icons/pi';

import { fetchWorkOrderList } from '#/api/momMgmt/workOrder/fetchList';
import { Button } from '#/modules/commonComponents/button';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import {
  createFixedMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import TableSearchInput from '#/modules/commonComponents/table/TableSearchInput';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import { createSelectColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';
import WorkOrderStatusBadge from '#/modules/momMgmt/workOrder/components/WorkOrderStatusBadge';
import { WORK_ORDER_STATUS_FILTER_OPTIONS } from '#/modules/momMgmt/workOrder/helper/workOrderStatus';
import useWorkOrderTransferWorkOrderPickerListQuery from '#/modules/momMgmt/workOrder/transfer/hooks/useWorkOrderTransferWorkOrderPickerListQuery';
import { formatDateOrDash } from '#/utils/date';
import { toast } from '#/utils/toast';
import { workOrderListQueryKey } from '#/utils/queryKeys';

const SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  workOrderNo: '工單號',
  status: '狀態',
  mtr: '成品',
  scheduledStartDate: '排程日',
  workWarehouse: '工作倉',
};

const EMPTY_PAGE = {
  current_page: 1,
  page_size: 10,
  total_count: 0,
  total_page: 1,
};

function displayDash(value) {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return value;
}

function formatMtrLabel(mtr) {
  if (!mtr) {
    return '-';
  }
  return mtr.mtrNo ? `${mtr.mtrNo} ${mtr.name ?? ''}`.trim() : displayDash(mtr.name);
}

function formatEntityLabel(entity) {
  if (!entity) {
    return '-';
  }
  const label = [entity.code, entity.name].filter(Boolean).join(' ');
  return label || '-';
}

function buildPickerColumns({
  filters = {},
  onFilterChange,
} = {}) {
  return [
    createSelectColumn({ enableMultiSelection: false }),
    {
      id: 'workOrderNo',
      header: '工單號',
      accessorKey: 'workOrderNo',
      accessorFn: (row) => displayDash(row.workOrderNo),
      size: 140,
      enableSorting: true,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'workOrderNo',
        label: '工單號',
        filterValue: filters.workOrderNo,
        onChange: (value) => onFilterChange('workOrderNo', value),
        placeholder: '搜尋工單號',
      }),
    },
    {
      id: 'status',
      header: '狀態',
      accessorKey: 'status',
      size: 100,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => (
        row.original.status
          ? <WorkOrderStatusBadge status={row.original.status} size="sm" />
          : '-'
      ),
      meta: {
        statusKind: 'document',
        ...createFixedMultiFilterMeta({
          key: 'status',
          label: '狀態',
          options: WORK_ORDER_STATUS_FILTER_OPTIONS,
          filterValue: filters.status,
          onChange: (value) => onFilterChange('status', value),
        }),
      },
    },
    {
      id: 'mtr',
      header: '成品',
      accessorFn: (row) => formatMtrLabel(row.mtr),
      size: 180,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'mtrNo',
          label: '成品',
          filterValue: filters.mtrNo,
          onChange: (value) => onFilterChange('mtrNo', value),
          placeholder: '搜尋料號或名稱',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={formatMtrLabel(row.original.mtr)} />
      ),
    },
    {
      id: 'scheduledStartDate',
      header: '排程日',
      accessorKey: 'scheduledStartDate',
      accessorFn: (row) => formatDateOrDash(row.scheduledStartDate),
      size: 120,
      enableSorting: true,
      enableColumnFilter: false,
    },
    {
      id: 'workWarehouse',
      header: '工作倉',
      accessorFn: (row) => formatEntityLabel(row.workWarehouse),
      size: 120,
      enableSorting: false,
      enableColumnFilter: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip
          text={formatEntityLabel(row.original.workWarehouse)}
        />
      ),
    },
  ];
}

function mapConfirmPayload(row) {
  const id = row?.id == null || row.id === '' ? '' : String(row.id);
  if (!id) {
    return null;
  }
  const fromRow = row?.workWarehouse?.id;
  const workWarehouseId = fromRow == null || fromRow === ''
    ? null
    : String(fromRow);
  return {
    id,
    workOrderNo: row?.workOrderNo == null ? '' : String(row.workOrderNo),
    workWarehouseId,
  };
}

/**
 * 工單轉倉：單選工單 dialog（ListTablePanel + 工單列表 API）。
 * 不需先選目標倉：確認後由工單工作倉帶入目標倉。
 */
const WorkOrderTransferWorkOrderPickerDialog = ({
  open,
  onOpenChange,
  selectedWorkOrderId = null,
  onConfirm,
}) => {
  const {
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetQuery,
  } = useWorkOrderTransferWorkOrderPickerListQuery();

  const [selectedById, setSelectedById] = useState({});
  const [searchKeyword, setSearchKeyword] = useState('');

  const listQueryResult = useQuery({
    queryKey: workOrderListQueryKey(listQuery),
    queryFn: () => fetchWorkOrderList(listQuery),
    enabled: open,
    placeholderData: keepPreviousData,
  });

  const rows = useMemo(
    () => listQueryResult.data?.data ?? [],
    [listQueryResult.data?.data],
  );

  const preselectedRow = useMemo(() => {
    if (!selectedWorkOrderId) {
      return null;
    }
    return rows.find((row) => String(row.id) === String(selectedWorkOrderId)) ?? null;
  }, [rows, selectedWorkOrderId]);

  const initialRowSelection = useMemo(() => {
    if (!preselectedRow) {
      return null;
    }
    return { [String(preselectedRow.id)]: true };
  }, [preselectedRow]);

  const effectiveSelectedById = useMemo(() => {
    if (Object.keys(selectedById).length > 0) {
      return selectedById;
    }
    if (preselectedRow) {
      return { [String(preselectedRow.id)]: preselectedRow };
    }
    return selectedById;
  }, [preselectedRow, selectedById]);

  const tablePanelKey = preselectedRow?.id
    ? `pick-${preselectedRow.id}`
    : 'pick-default';

  const handleFilterChange = useCallback((key, value) => {
    setFilter(key, value);
  }, [setFilter]);

  const columns = useMemo(() => buildPickerColumns({
    filters: listQuery.filters,
    onFilterChange: handleFilterChange,
  }), [listQuery.filters, handleFilterChange]);

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns),
    [columns],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowSelectionChange = useCallback((selection) => {
    setSelectedById(() => {
      const next = {};
      rows.forEach((row) => {
        const id = String(row.id);
        if (selection[id]) {
          next[id] = row;
        }
      });
      return next;
    });
  }, [rows]);

  function handleSearchSubmit() {
    setKeyword(searchKeyword);
  }

  function handleResetFilters() {
    setSearchKeyword('');
    resetQuery();
  }

  function handleConfirm() {
    const selected = Object.values(effectiveSelectedById).filter(Boolean);
    if (selected.length === 0) {
      toast.error('請選擇一張工單');
      return;
    }
    const payload = mapConfirmPayload(selected[0]);
    if (!payload) {
      toast.error('請選擇一張工單');
      return;
    }
    onConfirm(payload);
    handleOpenChange(false);
  }

  function handleCancel() {
    handleOpenChange(false);
  }

  function handleOpenChange(nextOpen) {
    if (!nextOpen) {
      resetQuery();
      setSearchKeyword('');
      setSelectedById({});
    }
    onOpenChange(nextOpen);
  }

  const headerSearch = (
    <div className="flex w-full max-w-md items-center gap-2 sm:w-72">
      <TableSearchInput
        useKeyword
        keyword={searchKeyword}
        onKeywordChange={setSearchKeyword}
        onSearch={handleSearchSubmit}
        placeholder="搜尋工單號、成品"
        className="min-w-0 w-full text-foreground"
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="secondary"
            size="icon"
            className="size-10 shrink-0 bg-panel text-foreground hover:bg-panel/90"
            aria-label="清除搜尋與篩選"
            onClick={handleResetFilters}
          >
            <PiArrowCounterClockwise size={20} aria-hidden />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">清除搜尋與篩選</TooltipContent>
      </Tooltip>
    </div>
  );

  const hasSelection = Object.keys(effectiveSelectedById).length > 0;

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="選擇工單"
      description="選擇一張工單後，目標倉帶入該工單工作倉，左欄需求量改為該工單需求。"
      headerVariant="primary"
      headerEnd={headerSearch}
      showCloseButton
      maxWidthClassName={dialogWideMaxWidthClassName}
      contentClassName={dialogContentCompactGapClassName}
      closeOnOutsideClick={false}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      confirmText="確認"
      cancelText="取消"
      confirmDisabled={listQueryResult.isLoading || !hasSelection}
    >
      <ListTablePanel
        key={tablePanelKey}
        variant="plain"
        columns={columns}
        rows={rows}
        pagination={listQueryResult.data?.page ?? EMPTY_PAGE}
        isLoading={listQueryResult.isLoading}
        isError={listQueryResult.isError}
        errorMessage="載入工單列表失敗"
        onRetry={() => listQueryResult.refetch()}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        listQuery={listQuery}
        filterStatusFields={filterStatusFields}
        sortFieldLabels={SORT_FIELD_LABELS}
        onFilterChange={handleFilterChange}
        onKeywordClear={() => {
          setSearchKeyword('');
          setKeyword('');
        }}
        onSortRemove={removeSortAt}
        onSortingChange={handleSortingChange}
        enableMultiSelection={false}
        paginationClassName="px-0 py-2"
        initialRowSelection={initialRowSelection}
        onRowSelectionChange={handleRowSelectionChange}
      />
    </AppDialog>
  );
};

WorkOrderTransferWorkOrderPickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  selectedWorkOrderId: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
};

export default WorkOrderTransferWorkOrderPickerDialog;
